/**
 * Feeds the CRM-wide overdue-invoice warning banner.
 *
 * Mounted once, in `components/crm-layout`, so it runs on EVERY CRM page. Two
 * consequences shape this hook:
 *
 *  1. It must be cheap and quiet. One request on mount, then a slow poll, a
 *     refetch when the tab regains focus, and a refetch when something else in
 *     the app settles an invoice (`OVERDUE_INVOICES_CHANGED_EVENT`).
 *  2. It must never fetch for someone who may not see the data. The banner is
 *     money, so it is gated on BOTH `invoices:access` and the financial wall
 *     (`financials:access`). A viewer missing either gets `enabled: false` — no
 *     request is made at all, so a 403 can never even be attempted.
 *
 * A failed poll is deliberately silent: the banner is an alert surface, and
 * turning a transient network blip into a red error bar on every CRM page would
 * be worse than showing nothing. The stale count simply stays until the next
 * successful read.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import usePermissions from '@crm-src/hooks/usePermissions'
import {
  OVERDUE_INVOICES_CHANGED_EVENT,
  fetchOverdueInvoices,
  markOverdueInvoicePaid,
  markOverdueInvoiceUnpaid,
  overdueSeverity,
  sortByUrgency,
  summarizeOverdue,
  type OverdueInvoice,
  type OverdueSeverity,
  type OverdueSummary,
} from '@crm-src/lib/overdue-invoices-api'

/** Slow on purpose — an overdue invoice does not change second to second, and
 * this runs on every CRM page for every signed-in operator. */
export const OVERDUE_POLL_INTERVAL_MS = 5 * 60 * 1000

export interface UseOverdueInvoicesResult {
  /** Oldest debt first. Empty while loading or when nothing is overdue. */
  invoices: OverdueInvoice[]
  summary: OverdueSummary
  severity: OverdueSeverity
  /** True only for the FIRST load — a background poll must not flash the UI. */
  loading: boolean
  refresh: () => Promise<void>
  markPaid: (id: number) => Promise<void>
  markUnpaid: (id: number) => Promise<void>
}

const EMPTY_SUMMARY: OverdueSummary = { count: 0, totalAmount: 0, maxDaysOverdue: 0 }

export function useOverdueInvoices(
  options: { pollIntervalMs?: number } = {},
): UseOverdueInvoicesResult {
  const { pollIntervalMs = OVERDUE_POLL_INTERVAL_MS } = options
  const { canAccessInvoices, canViewFinancials } = usePermissions()

  // The banner shows money about invoices — both gates must be open. Computed
  // every render (permissions arrive asynchronously after the CRM session
  // bootstraps, so this flips from false to true without a remount).
  const enabled = canAccessInvoices() && canViewFinancials()

  const [invoices, setInvoices] = useState<OverdueInvoice[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const hasLoadedRef = useRef(false)
  // Guards a setState after unmount (this lives in the layout, which unmounts
  // when the operator leaves the CRM section entirely).
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!enabled) {
      setInvoices([])
      setTotalCount(0)
      return
    }
    if (!hasLoadedRef.current) setLoading(true)
    try {
      const result = await fetchOverdueInvoices()
      if (!mountedRef.current) return
      setInvoices(result.invoices)
      setTotalCount(result.totalCount)
    } catch {
      // Silent by design — see the module comment. The previous (possibly
      // stale) count stays on screen rather than the banner vanishing or
      // turning into an error of its own.
    } finally {
      hasLoadedRef.current = true
      if (mountedRef.current) setLoading(false)
    }
  }, [enabled])

  // Initial load + whenever access flips on (permissions land after bootstrap).
  useEffect(() => {
    if (!enabled) {
      setInvoices([])
      setTotalCount(0)
      hasLoadedRef.current = false
      return
    }
    void refresh()
  }, [enabled, refresh])

  // Slow poll.
  useEffect(() => {
    if (!enabled || pollIntervalMs <= 0) return
    const id = setInterval(() => {
      void refresh()
    }, pollIntervalMs)
    return () => clearInterval(id)
  }, [enabled, pollIntervalMs, refresh])

  // Refetch when the operator comes back to the tab, and when any surface
  // reports an invoice payment landed.
  useEffect(() => {
    if (!enabled) return
    const onWake = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    const onChanged = () => {
      void refresh()
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onChanged)
    window.addEventListener(OVERDUE_INVOICES_CHANGED_EVENT, onChanged)
    return () => {
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onChanged)
      window.removeEventListener(OVERDUE_INVOICES_CHANGED_EVENT, onChanged)
    }
  }, [enabled, refresh])

  const markPaid = useCallback(async (id: number) => {
    await markOverdueInvoicePaid(id)
    // Drop it locally right away so the headline count falls the instant the
    // request resolves — the authoritative refetch happens when the drawer
    // closes.
    if (mountedRef.current) {
      setInvoices((current) => current.filter((inv) => inv.id !== id))
      setTotalCount((current) => Math.max(0, current - 1))
    }
  }, [])

  const markUnpaid = useCallback(async (id: number) => {
    await markOverdueInvoiceUnpaid(id)
    await refresh()
  }, [refresh])

  const sorted = useMemo(() => sortByUrgency(invoices), [invoices])
  const summary = useMemo(
    () => (enabled ? summarizeOverdue(sorted, totalCount) : EMPTY_SUMMARY),
    [enabled, sorted, totalCount],
  )
  const severity = useMemo(() => overdueSeverity(summary), [summary])

  return { invoices: sorted, summary, severity, loading, refresh, markPaid, markUnpaid }
}

export default useOverdueInvoices
